import { useState } from 'react'
import cn from 'clsx'

// Components
import { Avatar } from 'kit'
import {
  Dropdown,
  IconCloseFat,
  IconEnvelopeOutline,
  IconFavoriteOutline,
  IconGearOutline,
  IconProfileOutline,
  IconPurchasedTracksOutline,
  Link,
  useMaxWidth,
} from '@tracklib/kit'
import MobileMenu from 'components/mobilemenu'
import Credits from 'components/header/credits'

// Hooks
import { useProfile } from 'modules/profile/hooks'

import styles from './profilemenu.css'

type ProfileMenuProps = {
  onLogout: () => void
}

const ProfileMenu = ({ onLogout }: ProfileMenuProps): JSX.Element => {
  const profile = useProfile()
  const [open, toggleOpen] = useState(false)
  const isSmall = useMaxWidth()
  const sidebarHidden = useMaxWidth(1023)
  const listItems = [
    {
      icon: <IconProfileOutline />,
      label: 'Profile',
      to: '/profile/',
      datatesting: 'link_profile',
      mobileOrder: 1,
    },
    {
      icon: <IconGearOutline />,
      label: 'Settings',
      to: '/settings/',
      datatesting: 'link_settings',
      mobileOrder: 5,
    },
    {
      icon: <IconEnvelopeOutline className={styles.iconReferral} />,
      label: 'Referrals',
      to: '/referrals/',
      datatesting: 'link_referrals',
      mobileOrder: 4,
    },
  ]

  const mobileListItems = [
    {
      icon: <IconPurchasedTracksOutline />,
      label: 'Purchased Tracks',
      to: '/purchasedtracks/',
      datatesting: 'link_purchasedtracks',
      mobileOrder: 2,
    },
    {
      icon: <IconFavoriteOutline />,
      label: 'Favorites',
      to: '/favorites/',
      datatesting: 'link_favorites',
      mobileOrder: 3,
    },
  ]

  const renderLabel = () => (
    <div className={styles.label} datatesting="profile_menu">
      <Avatar disabled className={styles.avatar} />
    </div>
  )

  const items = (sidebarHidden ? [...listItems, ...mobileListItems] : listItems).sort((a, b) => a.mobileOrder - b.mobileOrder)
  if (isSmall && profile) {
    return (
      <>
        <button onClick={() => toggleOpen(!open)}>{renderLabel()}</button>
        <MobileMenu visible={open} toggle={toggleOpen} className={styles.menu}>
          <header className={styles.header}>
            <Link to="/profile" className={styles.profile} onClick={() => toggleOpen(false)}>
              <Avatar disabled className={styles.avatarImage} />
              <span className={styles.alias}>{profile.alias}</span>
            </Link>
            <button onClick={() => toggleOpen(false)} className={styles.closeButton}>
              <IconCloseFat />
            </button>
          </header>
          <Credits expanded />
          <div className={styles.linksWrapper}>
            <ul className={styles.links}>
              {items.map(({ to, icon, label, datatesting }) => {
                return (
                  <li datatesting="profile_menu_item" key={to}>
                    <Link to={to} className={styles.link} onClick={() => toggleOpen(false)} datatesting={datatesting}>
                      <span className={styles.icon}>{icon}</span> {label}
                    </Link>
                  </li>
                )
              })}
            </ul>
            <button onClick={onLogout} datatesting="button_log_out" className={styles.logout}>
              <span className={styles.icon} /> Log out
            </button>
          </div>
        </MobileMenu>
      </>
    )
  }

  return (
    <Dropdown
      active={open}
      className={styles.dropdown}
      label={renderLabel()}
      labelClassName={styles.labelButton}
      listClassName={styles.list}
      closesOnBackdropClick
      onToggle={() => toggleOpen(!open)}
      datatesting="button_profile_menu"
    >
      {items.map((listItem, index) => {
        const { to, icon, label, datatesting } = listItem
        return (
          <Dropdown.Item key={String(index)} datatesting="profile_menu_item" className={styles.item}>
            <Link to={to} className={styles.itemLink} onClick={() => toggleOpen(false)} datatesting={datatesting}>
              <span className={styles.itemIcon}>{icon}</span>
              <span>{label}</span>
            </Link>
          </Dropdown.Item>
        )
      })}
      <Dropdown.Item className={cn(styles.item, styles.logoutOption)}>
        <button onClick={onLogout} datatesting="button_log_out">
          Log out
        </button>
      </Dropdown.Item>
    </Dropdown>
  )
}

export default ProfileMenu
